import React from "react";
import { t } from "@auto/services";
import { can } from "@src/security/permissions";

import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { buildApplicantName, formatStatusCode } from "@src/lists/VoterApplicationList";
import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import { reportingResourceKey } from "@src/reporting/model/reportingFilters.mjs";
import { dateTime } from "@src/reporting/model/reportingFormatters.mjs";
import { loadReportingApplications } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const applicationTypeLabel = (value) => {
  if (Number(value) === 1) return t("voter.standard");
  if (Number(value) === 2) return t("voter.correction");
  return value == null ? "—" : String(value);
};

const ReportingApplicationsWidget = ({ gui, filter, refreshToken = 0 }) => {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const filterKey = reportingResourceKey("matching-applications", filter);

  React.useEffect(() => setPage(0), [filterKey]);

  const resource = useReportingResource(
    () => loadReportingApplications(filter, { offset: page * pageSize, limit: pageSize }),
    {
      key: reportingResourceKey(filterKey, page, pageSize),
      refreshToken,
      initialData: { total: 0, items: [], offset: 0, limit: pageSize },
    },
  );

  const data = resource.data || {};
  const rows = resource.error ? [] : (data.items || []);
  const total = resource.error ? 0 : Number(data.total || 0);
  const subtitle = [
    filter.dateFrom ? `${t("common.from")}: ${filter.dateFrom}` : null,
    filter.dateTo ? `${t("common.to")}: ${filter.dateTo}` : null,
  ].filter(Boolean).join(" · ");
  const canReadApplication = can(gui, "READ_VOTER_APPLICATION");

  return (
    <ReportingWidget
      title={t("reporting.matchingApplications")}
      subtitle={subtitle}
      loading={resource.loading}
      error={resource.error}
      empty={!resource.loading && resource.hasLoaded && rows.length === 0}
      emptyMessage={t("reporting.noMatchingApplications")}
    >
      <div className={styles.tableWrap}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("voter.submittedAt")}</TableCell>
                <TableCell>{t("person.nationalId")}</TableCell>
                <TableCell>{t("voter.applicant")}</TableCell>
                <TableCell>{t("voter.receiptNumber")}</TableCell>
                <TableCell>{t("voter.applicationType")}</TableCell>
                <TableCell>{t("common.status")}</TableCell>
                {canReadApplication ? <TableCell>{t("common.actions")}</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.gid}>
                  <TableCell>{dateTime(row.submittedAt)}</TableCell>
                  <TableCell>{row.nationalIdentifier || "—"}</TableCell>
                  <TableCell>{buildApplicantName(row) || "—"}</TableCell>
                  <TableCell>{row.receiptNumber || "—"}</TableCell>
                  <TableCell>{applicationTypeLabel(row.applicationType)}</TableCell>
                  <TableCell>{formatStatusCode(row.statusCode)}</TableCell>
                  {canReadApplication ? (
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                        onClick={() => gui.goTo(`/voter-application/${row.gid}`)}
                      >
                        {t("common.view")}
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[20, 50, 100]}
          labelRowsPerPage={t("reporting.rowsPerPage")}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
        />
      </div>
    </ReportingWidget>
  );
};

export default ReportingApplicationsWidget;
